/**
 * SIL-472 — Environmental Overview data layer. One hook per widget, each
 * returning `{ data, basis, isLoading, isError, error, refetch }` (the REAL
 * widgets) or a plain synchronous `{ data, basis }` (the SAMPLE widgets —
 * no network round trip, no loading state to model).
 *
 * REAL widgets share ONE underlying query
 * (`useEnvironmentalOverviewQuery`, `GET /api/client/environmental/overview`)
 * so the KPI strip, treemap, and monthly series never disagree about "as of
 * when" — the per-widget hooks below are thin selectors over that single
 * round trip, not independent fetches. SAMPLE widgets read
 * `../lib/environmentalSampleData.ts`, THE ONE MODULE their fixtures live
 * in — swapping a sample hook to a real query later is a one-line change
 * inside that hook, nothing else on the page.
 *
 * SIL-471: `useKpiAvoided`/`useAvoidedEventsTape` are now HYBRID — real when
 * the query's `avoided` field is non-null (the org has at least one
 * COMPLETED `marketplace_transaction`), sample fallback otherwise. The
 * fallback is detected by `avoided`'s NULLNESS alone (never by an empty
 * array or a zero total — see `dto/environmental-dashboard.ts`'s
 * `environmentalAvoidedSchema` doc comment), so a real org that genuinely
 * has zero avoided kgCO2e this period (implausible today, but not
 * impossible) would still render as real-zero, not silently swap to sample.
 *
 * SIL-485: `useScope3Cat11`/`useScope3Cat12` are likewise HYBRID — real
 * (basis `'derived'`) when the query's `s3MonthlyFlows` is non-null,
 * summing that array's per-period `cat11Kgco2e`/`cat12Kgco2e`, sample
 * fallback otherwise. Previously hardcoded to sample fixtures even though
 * `s3MonthlyFlows` (SIL-489) already carried the real per-category totals
 * for `MonthlySeriesChart`'s bottom panel — the seller cells in
 * `Scope3CategoryMatrix` just weren't wired to it.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  CarbonTargetsResponse,
  EnvironmentalAvoidedEvent,
  EnvironmentalBasis,
  EnvironmentalOverviewResponse,
  EnvironmentalYoyRow,
} from '@siliconx/shared';
import { api, throwIfNotOk } from '../lib/api';
import { qk } from '../lib/queryKeys';
import {
  SAMPLE_AVOIDED_EVENTS,
  SAMPLE_AVOIDED_KGCO2E,
  SAMPLE_AVOIDED_TRANSFER_COUNT,
  SAMPLE_BASIS,
  SAMPLE_SCOPE3_CAT11_INTENSITY_KGCO2E_PER_UNIT_YR,
  SAMPLE_SCOPE3_CAT11_KGCO2E,
  SAMPLE_SCOPE3_CAT12_KGCO2E,
  type SampleAvoidedEvent,
} from '../lib/environmentalSampleData';

// ---------------------------------------------------------------------------
// Shared underlying query — REAL widgets only.
// ---------------------------------------------------------------------------

export function useEnvironmentalOverviewQuery(): UseQueryResult<EnvironmentalOverviewResponse> {
  return useQuery({
    queryKey: qk.environmental.overview(),
    queryFn: async () => {
      const res = await api.api.client.environmental.overview.$get();
      await throwIfNotOk(res);
      return res.json();
    },
  });
}

type WidgetResult<T> = {
  data: T | undefined;
  basis: EnvironmentalBasis | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
};

// ---------------------------------------------------------------------------
// REAL — KPI strip: total / scope 2 / scope 3.
// ---------------------------------------------------------------------------

export function useKpiTotal(): WidgetResult<number> {
  const q = useEnvironmentalOverviewQuery();
  return {
    data: q.data?.kpis.totalKgco2e.valueKgco2e,
    basis: q.data?.kpis.totalKgco2e.basis,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
  };
}

export function useKpiScope2(): WidgetResult<number> {
  const q = useEnvironmentalOverviewQuery();
  return {
    data: q.data?.kpis.scope2Kgco2e.valueKgco2e,
    basis: q.data?.kpis.scope2Kgco2e.basis,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
  };
}

export function useKpiScope3(): WidgetResult<number> {
  const q = useEnvironmentalOverviewQuery();
  return {
    data: q.data?.kpis.scope3Kgco2e.valueKgco2e,
    basis: q.data?.kpis.scope3Kgco2e.basis,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
  };
}

// ---------------------------------------------------------------------------
// REAL — monthly S2+S3 stacked series + data-coverage strip.
// ---------------------------------------------------------------------------

export function useMonthlySeries(): WidgetResult<EnvironmentalOverviewResponse['monthlySeries']> & {
  coverage: EnvironmentalOverviewResponse['coverage'] | undefined;
} {
  const q = useEnvironmentalOverviewQuery();
  return {
    data: q.data?.monthlySeries,
    basis: q.data ? 'derived' : undefined,
    coverage: q.data?.coverage,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
  };
}

/**
 * SIL-489 — per-category monthly Scope 3 flow panel (small-multiples
 * chart's bottom panel). HYBRID like `useKpiAvoided`/`useAvoidedEventsTape`:
 * real when the query's `s3MonthlyFlows` is non-null, `null` data (the
 * caller falls back to `SAMPLE_S3_MONTHLY_FLOWS_KGCO2E`) otherwise — see
 * `dto/environmental-dashboard.ts`'s field doc comment for the nullability
 * contract this mirrors from PR #917's `avoided`.
 */
export function useS3MonthlyFlows(): {
  data: EnvironmentalOverviewResponse['s3MonthlyFlows'];
  basis: EnvironmentalBasis | undefined;
  isLoading: boolean;
} {
  const q = useEnvironmentalOverviewQuery();
  return {
    data: q.data?.s3MonthlyFlows ?? null,
    basis: q.data ? (q.data.s3MonthlyFlows ? 'derived' : 'sample') : undefined,
    isLoading: q.isLoading,
  };
}

/**
 * SIL-502 — amortized-embodied monthly management lens (top panel's second
 * series, alongside Scope 2). Unlike `useS3MonthlyFlows`, there is no SAMPLE
 * fallback here: `data` is `null` whenever the query's
 * `amortizedEmbodiedMonthlySeries` field is `null` (an org with no
 * derivable series), and `MonthlySeriesChart.tsx` simply omits the second
 * series in that case rather than fabricating one — see
 * `environmentalOverviewResponseSchema`'s field doc comment
 * (dto/environmental-dashboard.ts) for why this field has no sample fixture.
 */
export function useAmortizedEmbodiedMonthlySeries(): {
  data: EnvironmentalOverviewResponse['amortizedEmbodiedMonthlySeries'];
  basis: EnvironmentalBasis | undefined;
  isLoading: boolean;
} {
  const q = useEnvironmentalOverviewQuery();
  return {
    data: q.data?.amortizedEmbodiedMonthlySeries ?? null,
    basis: q.data?.amortizedEmbodiedMonthlySeries ? 'derived' : undefined,
    isLoading: q.isLoading,
  };
}

// ---------------------------------------------------------------------------
// HYBRID (SIL-471) — AVOIDED KPI tile + avoided-events tape: real when the
// shared query's `avoided` field is non-null, sample fallback otherwise.
// SAMPLE (unchanged) — YoY table: no serving surface yet, always
// synchronous fixtures — see environmentalSampleData.ts. (The INTENSITY KPI
// tile that used to live here was dropped in SIL-488 — sign-off decision,
// no serving surface, gpu-cohort-only meaning.)
// ---------------------------------------------------------------------------

export function useKpiAvoided(): {
  data: { valueKgco2e: number; transferCount: number };
  basis: EnvironmentalBasis;
  isLoading: boolean;
} {
  const q = useEnvironmentalOverviewQuery();
  if (q.data?.avoided) {
    return {
      data: { valueKgco2e: q.data.avoided.totalAvoidedKgco2e, transferCount: q.data.avoided.transferCount },
      basis: q.data.avoided.basis,
      isLoading: q.isLoading,
    };
  }
  return {
    data: { valueKgco2e: SAMPLE_AVOIDED_KGCO2E, transferCount: SAMPLE_AVOIDED_TRANSFER_COUNT },
    basis: SAMPLE_BASIS,
    isLoading: q.isLoading,
  };
}

/** Unified avoided-events tape row — the shape `AvoidedEventsTape.tsx`
 * renders, whichever source (real or sample) it came from. `verified` is
 * true ONLY for a real row whose transaction has a linked passport
 * (`EnvironmentalAvoidedEvent.passportId` non-null) — never true for a
 * SAMPLE row, which has no transaction to verify. */
export interface AvoidedEventView {
  id: string;
  timestamp: string;
  assetClass: string;
  model: string;
  event: string;
  avoidedKgco2e: number;
  verified: boolean;
}

function realAvoidedEventToView(e: EnvironmentalAvoidedEvent): AvoidedEventView {
  return {
    id: e.transactionId,
    timestamp: e.closedAt,
    assetClass: e.assetClassName,
    model: e.displayName,
    event: 'transfer settled',
    avoidedKgco2e: e.avoidedKgco2e,
    verified: e.passportId !== null,
  };
}

function sampleAvoidedEventToView(e: SampleAvoidedEvent): AvoidedEventView {
  return {
    id: e.id,
    timestamp: e.timestamp,
    assetClass: e.assetClass,
    model: e.model,
    event: e.event,
    avoidedKgco2e: e.avoidedKgco2e,
    verified: false,
  };
}

export function useAvoidedEventsTape(): { data: AvoidedEventView[]; basis: EnvironmentalBasis; isLoading: boolean } {
  const q = useEnvironmentalOverviewQuery();
  if (q.data?.avoided) {
    return {
      data: q.data.avoided.events.map(realAvoidedEventToView),
      basis: q.data.avoided.basis,
      isLoading: q.isLoading,
    };
  }
  return { data: SAMPLE_AVOIDED_EVENTS.map(sampleAvoidedEventToView), basis: SAMPLE_BASIS, isLoading: q.isLoading };
}

/**
 * REAL (SIL-487) — Year-over-year table. No sample fallback: `yoy` is `null`
 * only for an org with zero `asset_use_phase_snapshot` months ever, in which
 * case `data` is `undefined` and `YoyTable` renders its own empty state
 * (never a fixture — see environmentalSampleData.ts's module doc for why the
 * sample rows were deleted, not kept as a fallback).
 */
export function useYoyTable(): WidgetResult<EnvironmentalYoyRow[]> {
  const q = useEnvironmentalOverviewQuery();
  return {
    data: q.data?.yoy?.rows,
    basis: q.data?.yoy?.basis,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
  };
}

// ---------------------------------------------------------------------------
// SIL-475 — v0 Scope 3 upstream/downstream section.
// ---------------------------------------------------------------------------

/** REAL-derived — Category 4 (upstream transportation), the section's other
 * real row besides Cat 1/2 (which reuses `useKpiScope3` unchanged — the
 * overview's Scope 3 KPI already IS the Cat 1/2 embodied aggregate). Also
 * surfaces the two scalars the server used, so the UI can echo the formula
 * on hover instead of re-guessing the org's value. */
export function useScope3Cat4(): WidgetResult<number> & {
  usedTransportKm: number | undefined;
  freightFactorKgco2ePerTonneKm: number | undefined;
} {
  const q = useEnvironmentalOverviewQuery();
  return {
    data: q.data?.scope3.cat4Kgco2e.valueKgco2e,
    basis: q.data?.scope3.cat4Kgco2e.basis,
    usedTransportKm: q.data?.scope3.cat4UsedTransportKm,
    freightFactorKgco2ePerTonneKm: q.data?.scope3.cat4FreightFactorKgco2ePerTonneKm,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
  };
}

/**
 * HYBRID (SIL-485) — Category 11 (use of sold products), the seller matrix
 * cell. Real when the shared query's `s3MonthlyFlows` is non-null (same
 * nullability contract `useS3MonthlyFlows` reads above): the total is the
 * sum of every period's `cat11Kgco2e`, basis `'derived'`. There is no
 * per-org serving surface for the Box 11.2 use-phase-intensity figure (it
 * would require a per-asset annual-rate distribution the API doesn't
 * compute) — real rows report the total only, `intensityKgco2ePerUnitYr:
 * undefined`, and `Scope3CategoryMatrix` never rendered that field anyway
 * (only `valueKgco2e` reaches the seller cell). Sample fallback otherwise,
 * pairing the fixture total with its fixture intensity companion (see
 * `environmentalSampleData.ts`'s doc comment). */
export function useScope3Cat11(): {
  data: { valueKgco2e: number; intensityKgco2ePerUnitYr: number | undefined };
  basis: EnvironmentalBasis;
} {
  const q = useEnvironmentalOverviewQuery();
  const flows = q.data?.s3MonthlyFlows;
  if (flows) {
    return {
      data: {
        valueKgco2e: flows.reduce((sum, p) => sum + p.cat11Kgco2e, 0),
        intensityKgco2ePerUnitYr: undefined,
      },
      basis: 'derived',
    };
  }
  return {
    data: {
      valueKgco2e: SAMPLE_SCOPE3_CAT11_KGCO2E,
      intensityKgco2ePerUnitYr: SAMPLE_SCOPE3_CAT11_INTENSITY_KGCO2E_PER_UNIT_YR,
    },
    basis: SAMPLE_BASIS,
  };
}

/**
 * HYBRID (SIL-485) — Category 12 (end-of-life of sold products), always
 * paired with Cat 11 per the matrix ("a Cat 11 row without a Cat 12 row
 * invites the 'you hid the landfill' question"). Same `s3MonthlyFlows`
 * source and nullability contract as `useScope3Cat11` — sum of every
 * period's `cat12Kgco2e` when real, sample fixture otherwise. */
export function useScope3Cat12(): { data: { valueKgco2e: number }; basis: EnvironmentalBasis } {
  const q = useEnvironmentalOverviewQuery();
  const flows = q.data?.s3MonthlyFlows;
  if (flows) {
    return { data: { valueKgco2e: flows.reduce((sum, p) => sum + p.cat12Kgco2e, 0) }, basis: 'derived' };
  }
  return { data: { valueKgco2e: SAMPLE_SCOPE3_CAT12_KGCO2E }, basis: SAMPLE_BASIS };
}

/**
 * REAL (SIL-491) — buyer-lens persona values for the Variant B matrix's
 * buying-new / buying-used (SiliconX) columns, Cat 1/2 + Cat 4 rows. See
 * `environmentalScope3BuyerLensSchema`'s doc comment
 * (`dto/environmental-dashboard.ts`) for the full convention (asset-lineage
 * cohort split, the SIL-426 §7 ~0-embodied buy-used convention, the Cat 4
 * transport/refurb adders). Cat 8 has no per-acquisition split and stays a
 * badge in both buyer columns — this hook only ever backs Cat 1/2 and Cat 4.
 */
export function useScope3BuyerLens(): {
  data: EnvironmentalOverviewResponse['scope3']['buyerLens'] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
} {
  const q = useEnvironmentalOverviewQuery();
  return {
    data: q.data?.scope3.buyerLens,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
  };
}

// ---------------------------------------------------------------------------
// SIL-475 — org headline carbon_target, for the FY30 target card mount.
// Same `GET /sustainability/targets` endpoint the sustainability trajectory
// route and the scenario compare route already read (qk.sustainability.
// targets() — shared cache key, so this never double-fetches against those
// surfaces on the same page load).
// ---------------------------------------------------------------------------

export function useOrgCarbonTarget(): UseQueryResult<CarbonTargetsResponse> {
  return useQuery({
    queryKey: qk.sustainability.targets(),
    queryFn: async () => {
      const res = await api.api.client.sustainability.targets.$get();
      await throwIfNotOk(res);
      return res.json();
    },
  });
}
